// TODO: we use a similar component in templates TextWithMedia/UploadMedia.js
// UploadModal or/and UploadMedia could be merged
import React, { useState, useCallback, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { get } from 'lodash';

import Pficon from 'components/Pficon';
import { getSupportedMedia } from 'components/FileUpload/utils';

import { ICON_BOX_STYLE } from 'containers/QuestionnairePage/GenericQuestion/QuestionAnswerDropdownActions';
import { QA_ACTION_TYPES } from 'containers/QuestionnairePage/constants';
import UploadModal from './UploadModal';
import messages from './UploadModal/messages';
import { TEST_IDS_MEDIA } from './constants';

function UploadMedia(props) {
  const {
    uploadUrl,
    supportedMediaTypes,
    mediaValue = {},
    onAdd,
    onRemove,
    altText = '',
    disabled,
    testIdAdd = TEST_IDS_MEDIA.add,
    testIdEdit = TEST_IDS_MEDIA.edit,
    testIdRemove = TEST_IDS_MEDIA.remove,
    previewWidth = 24,
    previewHeight = 20,
    buttonStyle = {},
    getTooltipPopupContainer = (trigger) => trigger.parentNode,
    previewStyle = {},
    type = QA_ACTION_TYPES.BUTTON,
    hideAddButton = false,
    onOpen,
    addMessage = messages.addMedia,
    editMessage = messages.editMedia,
  } = props;

  const [modalIsVisible, setModalIsVisible] = useState(false);
  const supportedMedia = useMemo(
    () => getSupportedMedia(supportedMediaTypes),
    [supportedMediaTypes],
  );

  const showModal = useCallback(() => {
    setModalIsVisible(true);
    onOpen?.();
  }, [onOpen]);

  const closeModal = useCallback(() => {
    setModalIsVisible(false);
  }, []);

  const isListItem = type === QA_ACTION_TYPES.BUTTON_WITH_TEXT;

  if (isListItem) {
    return (
      <Fragment>
        <Button
          tabIndex="-1"
          className={classNames(
            'text-align-left',
            'border-none',
            'padding-horizontal-xxs',
            'shadow-none',
            'background-transparent',
            'background-transparent-hover',
            'w-full',
            'flex flex-items-center',
            {
              'color-blue-6-hover': !disabled,
              'color-grey-7 color-grey-7-hover': disabled,
            },
          )}
          disabled={disabled}
          onClick={showModal}
          data-testid={testIdAdd}
        >
          <Pficon
            className={classNames('text-size-secondary', 'margin-right-xxs', {
              'color-grey-7': disabled,
            })}
            size={16}
            style={ICON_BOX_STYLE}
            type="addimage2"
          />
          <span className="position-relative" style={{ top: 1 }}>
            <FormattedMessage
              {...(mediaValue.url ? editMessage : addMessage)}
            />
          </span>
        </Button>
        <UploadModal
          visible={modalIsVisible}
          title={
            <FormattedMessage
              {...(mediaValue.url ? editMessage : addMessage)}
            />
          }
          hideModal={closeModal}
          mask={false}
          media={mediaValue}
          saveMedia={onAdd}
          removeMedia={onRemove}
          supportedMedia={supportedMedia}
          uploadUrl={uploadUrl}
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      {Boolean(mediaValue.url) && (
        <div
          className={classNames(
            'background-grey-4',
            'border-radius-level-1',
            'text-align-center',
            'padding-tiny',
            'flex flex-items-center',
            'flex-justify-between',
            'position-relative',
          )}
          style={{
            ...previewStyle,
            width: previewWidth,
            height: previewHeight,
          }}
        >
          <button
            type="button"
            data-testid={testIdEdit}
            onClick={showModal}
            disabled={disabled}
            className={classNames(
              'outline-blue',
              'padding-none shadow-none outline-none flex flex-items-center',
              'w-full',
              {
                'cursor-pointer': !disabled,
                'cursor-disabled': disabled,
              },
            )}
            style={{ maxWidth: 36, height: 'auto' }}
          >
            <img
              src={mediaValue.thumbnail}
              alt={altText}
              className="w-full h-full"
              style={{
                objectFit: get(mediaValue, 'thumbnail', '').includes('.svg')
                  ? 'inherit'
                  : 'cover',
              }}
            />
          </button>
          <button
            type="button"
            data-testid={testIdRemove}
            onClick={onRemove}
            disabled={disabled}
            className={classNames(
              'flex',
              'flex-items-center',
              'padding-none',
              'position-absolute',
              'border-radius-level-1',
              {
                'cursor-pointer': !disabled,
                'cursor-disabled': disabled,
              },
            )}
            style={{
              top: -2,
              right: 0,
              backgroundColor: 'rgba(225, 230, 236, 0.7)',
            }}
          >
            <Pficon
              type="close"
              className="color-grey-10 color-grey-8-hover transition-normal"
              style={{ fontSize: 9 }}
            />
          </button>
        </div>
      )}

      {!mediaValue.url && !hideAddButton && (
        <div
          className={classNames(
            'flex',
            'flex-col',
            'flex-align-items--center',
            'flex-justify-center',
          )}
          style={{ height: 32 }}
        >
          <OptionalTooltip
            placement="topRight"
            arrowPointAtCenter
            overlayClassName="tooltip-light"
            trigger="hover"
            title="Add media"
            disabled={disabled}
            getPopupContainer={getTooltipPopupContainer}
            overlayStyle={{ zIndex: 1000 }}
          >
            <Button
              data-testid={testIdAdd}
              className={classNames(
                'outline-blue',
                'border-none',
                'padding-none',
                'background-transparent',
                'text-size-h5',
                {
                  'color-grey-7': disabled,
                  'color-grey-7-hover': disabled,
                },
                {
                  'color-grey-10 color-blue-6-hover': !disabled,
                },
              )}
              style={{
                height: 12,
                ...buttonStyle,
              }}
              disabled={disabled}
              ghost
              onClick={showModal}
            >
              <Pficon type="addimage2" />
            </Button>
          </OptionalTooltip>
        </div>
      )}

      <UploadModal
        visible={modalIsVisible}
        title={<FormattedMessage {...messages.addMedia} />}
        hideModal={closeModal}
        mask={false}
        media={mediaValue}
        saveMedia={onAdd}
        removeMedia={onRemove}
        supportedMedia={supportedMedia}
        uploadUrl={uploadUrl}
      />
    </Fragment>
  );
}

UploadMedia.propTypes = {
  uploadUrl: PropTypes.string,
  supportedMediaTypes: PropTypes.array,
  mediaValue: PropTypes.object,
  altText: PropTypes.string,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  disabled: PropTypes.bool,
  buttonStyle: PropTypes.object,
  type: PropTypes.string,
  hideAddButton: PropTypes.bool,
  onOpen: PropTypes.func,
  addMessage: PropTypes.object,
  editMessage: PropTypes.object,
};

export default UploadMedia;

const OptionalTooltip = (props) => {
  if (props.disabled) {
    return props.children;
  }
  return <Tooltip {...props} />;
};
